/**
 * CarrierEquipmentPage — /carrier/equipment
 * Carrier admin fleet management: tractors, trailers, combos
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { carrierApi, type CarrierEquipmentUnit } from '../lib/api'
import { useAuth } from '../lib/auth-context'
import { SupportReadOnlyNotice, useSupportReadOnlyView } from '../lib/support-session'

const UNIT_TYPE_LABELS: Record<string, string> = { tractor: '🚛 Tractor', trailer: '🚚 Trailer', combo: '🔗 Combo' }
const STATUS_COLORS: Record<string, string> = {
  ACTIVE:   'bg-green-100 text-green-800',
  INACTIVE: 'bg-gray-100 text-gray-600',
  IN_SHOP:  'bg-amber-100 text-amber-800',
  RETIRED:  'bg-red-100 text-red-600',
}

function ExpiryBadge({ date, label }: { date?: string | null; label: string }) {
  if (!date) return <span className="text-xs text-gray-400">—</span>
  const exp = new Date(date); const now = new Date()
  const days = Math.round((exp.getTime() - now.getTime()) / 86400000)
  const cls = days < 0 ? 'text-red-700 font-semibold' : days <= 30 ? 'text-amber-700' : 'text-gray-600'
  return <span className={`text-xs ${cls}`}>{label}: {date}{days < 0 ? ' ⚠ EXPIRED' : days <= 30 ? ` (${days}d)` : ''}</span>
}

interface UnitFormData {
  unitType: 'tractor' | 'trailer' | 'combo'
  unitNumber: string
  displayName: string
  vin: string
  year: string
  make: string
  model: string
  trim: string
  plateNumber: string
  plateState: string
  ownershipType: string
  registrationExpires: string
  inspectionExpires: string
  insuranceExpires: string
  notes: string
}

const emptyForm = (): UnitFormData => ({
  unitType: 'tractor', unitNumber: '', displayName: '',
  vin: '', year: '', make: '', model: '', trim: '',
  plateNumber: '', plateState: '', ownershipType: 'OWNED',
  registrationExpires: '', inspectionExpires: '', insuranceExpires: '', notes: '',
})

const inputCls = 'w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'

function UnitModal({
  unit,
  onClose,
  onSaved,
}: {
  unit?: CarrierEquipmentUnit | null
  onClose: () => void
  onSaved: (message: string) => void
}) {
  const qc = useQueryClient()
  const [form, setForm] = useState<UnitFormData>(() => unit ? {
    unitType: unit.unitType as any,
    unitNumber:          unit.unitNumber ?? '',
    displayName:         unit.displayName ?? '',
    vin:                 unit.vin ?? '',
    year:                unit.year?.toString() ?? '',
    make:                unit.make ?? '',
    model:               unit.model ?? '',
    trim:                unit.trim ?? '',
    plateNumber:         unit.plateNumber ?? '',
    plateState:          unit.plateState ?? '',
    ownershipType:       unit.ownershipType ?? 'OWNED',
    registrationExpires: unit.registrationExpires ?? '',
    inspectionExpires:   unit.inspectionExpires ?? '',
    insuranceExpires:    unit.insuranceExpires ?? '',
    notes:               unit.notes ?? '',
  } : emptyForm())
  const [err, setErr] = useState<string | null>(null)

  const f = (k: keyof UnitFormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(prev => ({ ...prev, [k]: e.target.value }))

  const mut = useMutation({
    mutationFn: () => {
      const data = {
        ...form,
        year: form.year ? Number(form.year) : undefined,
      }
      return unit
        ? carrierApi.updateEquipment(unit.id, data)
        : carrierApi.createEquipment({ ...data, unitType: form.unitType })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['carrier-equipment'] })
      onSaved(unit ? 'Equipment unit updated successfully.' : 'Equipment unit added successfully.')
      onClose()
    },
    onError: (e: any) => setErr(e?.response?.data?.error ?? e.message),
  })

  const validateBeforeSave = (): string | null => {
    if (!form.unitType) return 'Unit Type is required.'
    if (!form.unitNumber.trim() && !form.displayName.trim()) return 'Unit Number or Display Name is required.'
    if (form.year && (!Number.isFinite(Number(form.year)) || Number(form.year) < 1900 || Number(form.year) > 2100)) {
      return 'Year must be between 1900 and 2100.'
    }
    return null
  }

  const handleSave = () => {
    const validationError = validateBeforeSave()
    if (validationError) {
      setErr(validationError)
      return
    }
    setErr(null)
    mut.mutate()
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-2xl my-8">
        <h2 className="text-lg font-bold text-gray-900 mb-4">{unit ? 'Edit Unit' : 'Add Equipment Unit'}</h2>
        {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm mb-4">{err}</div>}
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Unit Type</label>
            <select value={form.unitType} onChange={f('unitType')} className={inputCls}>
              <option value="tractor">Tractor</option>
              <option value="trailer">Trailer</option>
              <option value="combo">Combo (tractor + trailer unit)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Unit Number</label>
            <input value={form.unitNumber} onChange={f('unitNumber')} placeholder="T-42" className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Display Name</label>
            <input value={form.displayName} onChange={f('displayName')} placeholder="Volvo T-42 (2021)" className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Year</label>
            <input type="number" value={form.year} onChange={f('year')} placeholder="2021" className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Make</label>
            <input value={form.make} onChange={f('make')} placeholder="Volvo" className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Model</label>
            <input value={form.model} onChange={f('model')} placeholder="VAH 600" className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">VIN</label>
            <input value={form.vin} onChange={f('vin')} placeholder="1VUJGGAH..." className={inputCls} maxLength={17} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Plate Number</label>
            <input value={form.plateNumber} onChange={f('plateNumber')} placeholder="ABC1234" className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Plate State</label>
            <input value={form.plateState} onChange={f('plateState')} placeholder="TX" maxLength={2} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Ownership</label>
            <select value={form.ownershipType} onChange={f('ownershipType')} className={inputCls}>
              <option value="OWNED">Owned</option>
              <option value="LEASED">Leased</option>
              <option value="RENTAL">Rental</option>
              <option value="OWNER_OPERATOR">Owner-Operator</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Registration Expires</label>
            <input type="date" value={form.registrationExpires} onChange={f('registrationExpires')} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Inspection Expires</label>
            <input type="date" value={form.inspectionExpires} onChange={f('inspectionExpires')} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Insurance Expires</label>
            <input type="date" value={form.insuranceExpires} onChange={f('insuranceExpires')} className={inputCls} />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
            <textarea value={form.notes} onChange={f('notes')} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={handleSave} disabled={mut.isPending}
            className="bg-blue-700 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-blue-800 disabled:opacity-50">
            {mut.isPending ? 'Saving…' : unit ? 'Save Changes' : 'Add Unit'}
          </button>
          <button onClick={onClose} className="text-sm text-gray-600 hover:text-gray-900 px-4 py-2.5 border rounded-lg">Cancel</button>
        </div>
      </div>
    </div>
  )
}

export default function CarrierEquipmentPage() {
  const { hasRole } = useAuth()
  const { isSupportReadOnlyView } = useSupportReadOnlyView()
  const qc = useQueryClient()
  const [editUnit, setEditUnit] = useState<CarrierEquipmentUnit | null | undefined>(undefined)
  const [filter, setFilter] = useState<string>('')
  const [typeFilter, setTypeFilter] = useState<string>('')
  const [saveNotice, setSaveNotice] = useState<string | null>(null)

  const canEdit = hasRole('ADMIN', 'AUX_ADMIN', 'SUPER_ADMIN', 'CARRIER_ADMIN', 'DISPATCHER') && !isSupportReadOnlyView
  const canView = hasRole('ADMIN', 'AUX_ADMIN', 'SUPER_ADMIN', 'CARRIER_ADMIN', 'DISPATCHER')
  const canFinanceView = hasRole('ADMIN', 'AUX_ADMIN', 'SUPER_ADMIN', 'CARRIER_ADMIN', 'DISPATCHER')

  if (!canView) {
    return <div className="p-6 text-red-600 text-sm">Access denied.</div>
  }

  const unitsQ = useQuery({
    queryKey: ['carrier-equipment', typeFilter],
    queryFn: () => carrierApi.listEquipment(typeFilter ? { type: typeFilter } : {}).then(r => r.data.units),
  })

  const retireMut = useMutation({
    mutationFn: (id: string) => carrierApi.retireEquipment(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['carrier-equipment'] }),
  })

  const units = (unitsQ.data ?? []).filter(u => {
    if (!filter) return true
    const q = filter.toLowerCase()
    return [u.unitNumber, u.displayName, u.make, u.model, u.vin, u.plateNumber].some(f => f?.toLowerCase().includes(q))
  })

  const tractors = units.filter(u => u.unitType === 'tractor')
  const trailers = units.filter(u => u.unitType === 'trailer')
  const combos   = units.filter(u => u.unitType === 'combo')

  function renderUnits(group: CarrierEquipmentUnit[], label: string) {
    if (!group.length) return null
    return (
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">{label}</h3>
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase">Unit</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase">Make/Model</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase">Plate</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase">Expiry Dates</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase">Status</th>
                {canEdit && <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {group.map(unit => (
                <tr key={unit.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{unit.unitNumber ?? '—'}</div>
                    {unit.displayName && <div className="text-xs text-gray-500">{unit.displayName}</div>}
                    {unit.vin && <div className="text-xs font-mono text-gray-400">{unit.vin}</div>}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {[unit.year, unit.make, unit.model].filter(Boolean).join(' ') || <span className="text-gray-400">—</span>}
                    {unit.trim && <div className="text-xs text-gray-400">{unit.trim}</div>}
                    {(unit.specManufacturer || unit.specModel) && (
                      <div className="text-xs text-blue-600">📋 {unit.specManufacturer} {unit.specModel}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    {unit.plateNumber ? `${unit.plateNumber} (${unit.plateState ?? '??'})` : '—'}
                    {unit.ownershipType && unit.ownershipType !== 'OWNED' && (
                      <div className="text-gray-400">{unit.ownershipType}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 space-y-0.5">
                    <ExpiryBadge date={unit.inspectionExpires}   label="Insp" />
                    <br />
                    <ExpiryBadge date={unit.insuranceExpires}    label="Ins" />
                    <br />
                    <ExpiryBadge date={unit.registrationExpires} label="Reg" />
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-xs px-2 py-0.5 rounded font-medium ${STATUS_COLORS[unit.unitStatus] ?? ''}`}>
                      {unit.unitStatus}
                    </span>
                  </td>
                  {canEdit && (
                    <td className="px-4 py-3 text-right space-x-2">
                      <button onClick={() => setEditUnit(unit)}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium">Edit</button>
                      {unit.unitStatus !== 'RETIRED' && (
                        <button onClick={() => { if (confirm(`Retire ${unit.unitNumber ?? unit.id}?`)) retireMut.mutate(unit.id) }}
                          className="text-xs text-red-500 hover:text-red-700 font-medium">Retire</button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Fleet Equipment</h1>
          <p className="text-sm text-gray-500 mt-1">Manage tractors, trailers, and combo units</p>
        </div>
        <div className="flex items-center gap-2">
          {canFinanceView && (
            <Link to="/carrier/fleet/finance" className="text-sm text-blue-700 hover:text-blue-900 font-medium px-4 py-2.5 border border-blue-200 rounded-lg">
              Finance →
            </Link>
          )}
          {canEdit && (
            <button onClick={() => setEditUnit(null)}
              className="bg-blue-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg hover:bg-blue-800">
              + Add Unit
            </button>
          )}
        </div>
      </div>

      {isSupportReadOnlyView && <SupportReadOnlyNotice className="mb-4" />}

      {/* Filters */}
      <div className="flex gap-3 mb-5">
        <input value={filter} onChange={e => setFilter(e.target.value)}
          placeholder="Search unit #, make, model, VIN…"
          className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64" />
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All types</option>
          <option value="tractor">Tractors</option>
          <option value="trailer">Trailers</option>
          <option value="combo">Combos</option>
        </select>
      </div>

      {saveNotice && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
          {saveNotice}
        </div>
      )}

      {unitsQ.isLoading && <div className="text-gray-400 text-sm py-8 text-center">Loading equipment…</div>}

      {unitsQ.isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Failed to load equipment. {(unitsQ.error as any)?.response?.data?.error ?? (unitsQ.error as any)?.message ?? 'Please try again.'}
        </div>
      )}

      {!unitsQ.isLoading && units.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <div className="text-4xl mb-3">🚛</div>
          <p className="text-sm">No equipment units found.</p>
          {canEdit && <p className="text-xs mt-1">Click "Add Unit" to add your first tractor or trailer.</p>}
        </div>
      )}

      {renderUnits(tractors, `Tractors (${tractors.length})`)}
      {renderUnits(trailers, `Trailers (${trailers.length})`)}
      {renderUnits(combos,   `Combos (${combos.length})`)}

      <p className="text-xs text-gray-400 mt-6">
        Units with status ACTIVE can be assigned to drivers. Retired units are hidden from assignment dropdowns.
      </p>

      {editUnit !== undefined && (
        <UnitModal
          unit={editUnit}
          onClose={() => setEditUnit(undefined)}
          onSaved={(message) => setSaveNotice(message)}
        />
      )}
    </div>
  )
}
